import * as L from "leaflet";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { GeoJSON, WMSTileLayer } from "react-leaflet";

import { PROJECTIONS } from "@/components/places/constants";
import { useAppSelector } from "@/redux/store/hooks";
import { transformGeoJSON } from "./helper";

const Layers = () => {
  const layers = useAppSelector((state) => state.placeLayers.layers);
  const [layerGeoJSONData, setLayerGeoJSONData] = useState({});

  const fetchGeoJSON = async (url) => {
    const response = await fetch(url);
    const data = await response.json();
    return data;
  };

  const getGeoJSONData = () => {
    layers?.map(async (layer, index) => {
      if (layer.category === "GeoJSON" && layer?.url) {
        let geojson = await fetchGeoJSON(layer.url);

        const crsName = geojson?.crs?.properties?.name;
        const crsVal = crsName?.split(":")?.pop() || "4326";
        const isCrs =
          !crsName?.includes("CRS84") || !crsName?.includes("4326") || !crsName;

        geojson = transformGeoJSON(
          geojson,
          isCrs,
          PROJECTIONS[`${crsVal}`],
          PROJECTIONS["4326"]
        );

        setLayerGeoJSONData((prevData) => {
          return {
            ...prevData,
            [index]: {
              type: "geojson",
              data: geojson,
              metadata: layer.metadata,
              hidden: layer.hidden
            }
          };
        });
      }

      if (layer.category.includes("WMS")) {
        setLayerGeoJSONData((prevData) => {
          return {
            ...prevData,
            [index]: {
              type: "wms",
              data: layer.url,
              metadata: layer.metadata,
              hidden: layer.hidden
            }
          };
        });
      }
    });
  };

  useEffect(() => {
    getGeoJSONData();
  }, [layers]);

  const getMetaStyle = (geoJsonFeature: any, layer: any) => {
    const geometryType = geoJsonFeature.geometry.type;

    const { color, opacity, pointRadius } =
      layerGeoJSONData[layer].metadata.featureCollection;

    switch (geometryType) {
      case "MultiPolygon":
      case "Polygon":
        return {
          color,
          fillOpacity: opacity,
          weight: pointRadius
        };
      case "MultiLineString":
      case "LineString":
        return {
          color,
          weight: pointRadius
        };

      case "Point":
        return {
          color, // stroke color if stroke is true
          stroke: true,
          opacity,
          fillOpacity: opacity,
          radius: pointRadius
        };
    }
  };

  const layersKeys = Object.keys(layerGeoJSONData);
  const hasAllLayersLoaded = layersKeys.length === layers.length;

  const onEachFeature = (feature, layer, metadata) => {
    const { properties } = feature;
    const { propertyVisibility } = metadata;

    // No popup for custom layer
    if (isEmpty(properties)) return;

    const popupOptions = {
      minWidth: 100,
      maxWidth: 250,
      className: "popup-classname highlight-layer"
    };

    layer.bindPopup(() => {
      const allProperties = Object.keys(properties);
      const allSelectedProperties = allProperties.filter(
        (property) => propertyVisibility[property]
      );
      const tableData = allSelectedProperties
        .map((item) => {
          const showProperty =
            properties?.[item] !== undefined && properties?.[item] !== null;

          return showProperty
            ? `<tr><th>${item}: </th><td>${properties?.[item]}</td></tr>`
            : null;
        })
        .filter((n) => n);

      return `<table>${tableData.join("")}</table>`;
    }, popupOptions);
  };

  const renderAllLayers = () => {
    return layersKeys.map((layer, index) => {
      const {
        type,
        data: url,
        metadata: { wmsLayerName },
        hidden
      } = layerGeoJSONData[layer];

      if (hidden) return;

      if (type === "geojson") {
        const metadata = layerGeoJSONData[layer].metadata;

        return (
          <GeoJSON
            key={index}
            data={layerGeoJSONData[layer].data}
            onEachFeature={(feature, layer) =>
              onEachFeature(feature, layer, metadata)
            }
            style={(geoJsonFeature) => getMetaStyle(geoJsonFeature, layer)}
            pointToLayer={(_geoJsonPoint, latlng) => {
              return L.circleMarker(latlng);
            }}
          />
        );
      }

      if (type === "wms") {
        // eslint-disable-next-line no-undef
        const urlData = new URL(url);

        return (
          <WMSTileLayer
            key={index}
            url={`${urlData.origin}${urlData.pathname}`}
            params={{
              layers: wmsLayerName,
              format: "image/png",
              transparent: true,
              request: "GetMap",
              service: "WMS"
            }}
          />
        );
      }
    });
  };

  if (hasAllLayersLoaded) {
    return <>{renderAllLayers()}</>;
  }

  return null;
};

export default Layers;
